import React from 'react';
import { NavLink, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import ClientLandingPage from './ClientLandingPage';
import ClientSeoPage from './ClientSeoPage';
import ClientResearchPage from './ClientResearchPage';
import ClientResearchPageBeta from './ClientResearchPageBeta';
import ClientGeneralInfoPage from './ClientGeneralInfoPage';
import { ClientData } from '../types';
import { ClientService } from '../services/clientService';

interface ClientDashboardProps {
  clientData: ClientData;
  onRequestAdmin?: () => void;
}

const ActiveNavLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <NavLink
    to={to}
    end={to === ''}
    className={({ isActive }) =>
      `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
        isActive ? 'bg-white text-slate-900 shadow-md' : 'text-white/80 hover:text-white hover:bg-white/10'
      }`
    }
  >
    {children}
  </NavLink>
);

export default function ClientDashboard({ clientData, onRequestAdmin }: ClientDashboardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = clientData.uiSettings?.theme;
  const backgroundColor = theme?.background || '#0f172a';

  // State for audience-specific data (used by research-beta page)
  const [audienceData, setAudienceData] = React.useState<ClientData>(clientData);
  const [isLoadingAudience, setIsLoadingAudience] = React.useState(false);

  // Update audienceData when clientData changes
  React.useEffect(() => {
    setAudienceData(clientData);
  }, [clientData]);

  // Handle audience change for beta research page
  const handleAudienceChange = async (audienceId: string) => {
    if (audienceId === audienceData.currentAudienceId) return;

    setIsLoadingAudience(true);
    try {
      const newData = await ClientService.getClientBySlug(clientData.slug, audienceId);
      if (newData) {
        setAudienceData(newData);
      }
    } catch (error) {
      console.error('Failed to load audience data:', error);
    } finally {
      setIsLoadingAudience(false);
    }
  };

  const navSettings = clientData.uiSettings?.nav;
  const showOverview = navSettings?.overview !== false;
  const showSeo = navSettings?.seo !== false;
  const showResearch = navSettings?.research !== false;
  const showGeneralInfo = navSettings?.generalInfo === true;

  // Determine the default/home page when overview is hidden
  const getDefaultPath = () => {
    if (showOverview) return '';
    if (showResearch) return 'research';
    if (showSeo) return 'seo';
    if (showGeneralInfo) return 'additional-insights';
    return '';
  };
  const defaultPath = getDefaultPath();

  const navItems = [
    { path: showOverview ? '' : 'overview', label: 'Overview', visible: showOverview },
    { path: 'seo', label: 'SEO / AEO / GEO', visible: showSeo },
    { path: showOverview ? 'research' : '', label: 'Research Insights', visible: showResearch },
    { path: 'additional-insights', label: 'Additional Insights', visible: showGeneralInfo },
  ].filter((item) => item.visible);

  React.useEffect(() => {
    const token = `Customer insights for ${clientData.name}`;
    document.title = token;
  }, [clientData?.name]);

  React.useEffect(() => {
    const validPaths = ['', '/', '/overview', '/seo', '/research', '/research-beta', '/additional-insights'];
    const segments = location.pathname.split('/').filter(Boolean);
    const subPath = segments.slice(1).join('/');
    const normalized = subPath ? `/${subPath}` : '/';
    if (!validPaths.includes(normalized)) {
      navigate(defaultPath, { replace: true });
    }
  }, [location.pathname, navigate, defaultPath]);

  return (
    <div className="min-h-screen" style={{ backgroundColor }}>
      <header className="bg-slate-900/65 backdrop-blur-md border-b border-white/10 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-6 flex flex-col items-center md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            {clientData.logo && (
              <img
                src={clientData.logo}
                alt={`${clientData.name} Logo`}
                className="h-12 md:h-14 w-auto object-contain"
              />
            )}
            <div>
              <div className="text-xs uppercase tracking-[0.45em] text-white/60">Client Insights</div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">{clientData.name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-4 w-full md:w-auto justify-center md:justify-end">
            {/* Only show navigation if more than 1 item is visible */}
            {navItems.length > 1 && (
              <nav className="flex justify-center md:justify-start items-center gap-2 bg-white/10 border border-white/10 rounded-full px-2 py-1 w-full md:w-auto">
                {navItems.map((item) => (
                  <ActiveNavLink key={item.path} to={item.path}>
                    {item.label}
                  </ActiveNavLink>
                ))}
              </nav>
            )}
            {onRequestAdmin && (
              <button
                type="button"
                onClick={onRequestAdmin}
                className="inline-flex items-center text-sm font-semibold text-white/80 hover:text-white transition-colors"
              >
                Admin
              </button>
            )}
          </div>
        </div>
      </header>

      <main>
        <Routes>
          {/* Index route: Show first available page in preferred order */}
          <Route
            index
            element={
              showOverview ? (
                <ClientLandingPage clientData={clientData} />
              ) : showResearch ? (
                <ClientResearchPage clientData={clientData} onRequestAdmin={onRequestAdmin} />
              ) : showSeo ? (
                <ClientSeoPage clientData={clientData} />
              ) : (
                <ClientGeneralInfoPage clientData={clientData} />
              )
            }
          />
          {/* Explicit routes for each page */}
          {showOverview && <Route path="research" element={<ClientResearchPage clientData={clientData} onRequestAdmin={onRequestAdmin} />} />}
          {!showOverview && <Route path="overview" element={<ClientLandingPage clientData={clientData} />} />}
          <Route path="seo" element={<ClientSeoPage clientData={clientData} />} />
          <Route path="additional-insights" element={<ClientGeneralInfoPage clientData={clientData} />} />
          <Route
            path="research-beta"
            element={
              <ClientResearchPageBeta
                clientData={audienceData}
                onRequestAdmin={onRequestAdmin}
                onAudienceChange={handleAudienceChange}
              />
            }
          />
          <Route
            path="*"
            element={
              showOverview ? (
                <ClientLandingPage clientData={clientData} />
              ) : showResearch ? (
                <ClientResearchPage clientData={clientData} onRequestAdmin={onRequestAdmin} />
              ) : showSeo ? (
                <ClientSeoPage clientData={clientData} />
              ) : (
                <ClientGeneralInfoPage clientData={clientData} />
              )
            }
          />
        </Routes>
      </main>
    </div>
  );
}
